import {useState} from 'react'
import {useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {HelpIcon} from '@/components/help-icon'
import {PerfilCargoChrome} from '@/components/perfil-cargo-chrome'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {useOperatorPage} from '@/components/no-access-state'
import {usePerfilCargo, useVersionesPerfil} from '@/hooks/empresa/perfil_cargo/use-perfiles-cargo'
import {formatInstantDate} from '@/lib/risk'

export function PerfilCargoVersionesPage() {
  const {t} = useTranslation()
  const {businessId, positionId} = useParams()
  const {business, guard} = useOperatorPage(businessId)
  const [page, setPage] = useState(1)
  const perfil = usePerfilCargo(businessId, positionId)
  const versiones = useVersionesPerfil(business ? businessId : undefined, positionId, page)
  if (guard) return guard
  return (
    <PerfilCargoChrome
      businessId={businessId!}
      positionId={positionId!}
      cargo={perfil.data?.cargo}
      testId="perfil-cargo-versiones-page">
      <section className="space-y-2" data-testid="perfil-versiones">
        <h2 className="flex items-center gap-1.5 font-heading text-base tracking-tight">
          {t('perfilCargo.versiones.titulo')}
          <HelpIcon
            label={t('common.help.iconLabelFor', {campo: t('perfilCargo.versiones.titulo')})}
            testId="perfil-versiones-ayuda">
            {t('perfilCargo.versiones.lecturaInversa')}
          </HelpIcon>
        </h2>
        {versiones.isPending && <Skeleton className="h-16 w-full" data-testid="perfil-versiones-cargando" />}
        {versiones.isError && (
          <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="perfil-versiones-error">
            {t('perfilCargo.versiones.errorCarga')}
          </p>
        )}
        {versiones.data?.items.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="perfil-versiones-vacio">
            {t('perfilCargo.versiones.vacio')}
          </p>
        )}
        <ul className="space-y-2">
          {versiones.data?.items.map((version) => (
            <li key={version.version} className="text-sm" data-testid={`version-${version.version}`}>
              <Tag tone={version.estado === 'confirmado' ? 'green' : 'amber'}>
                {t('perfilCargo.versiones.version')} {version.version}
              </Tag>{' '}
              {version.confirmado_por_nombre && version.confirmado_en && (
                <span className="text-muted-foreground">
                  {t('perfilCargo.confirmado.rotulo', {
                    nombre: version.confirmado_por_nombre,
                    fecha: formatInstantDate(version.confirmado_en),
                  })}
                </span>
              )}{' '}
              <span className="text-muted-foreground">
                · {version.lineas} {t('perfilCargo.versiones.lineas').toLowerCase()}
              </span>
            </li>
          ))}
        </ul>
        {versiones.data && (
          <PaginationBar
            page={page}
            pageSize={versiones.data.page_size}
            total={versiones.data.total}
            onPageChange={setPage}
          />
        )}
        {/* `null` es «todavía no hay versión de la que decirlo», no cero. */}
        {perfil.data?.solicitudes !== null && perfil.data?.solicitudes !== undefined && (
          <p className="text-sm text-muted-foreground" data-testid="perfil-solicitudes">
            {t('perfilCargo.confirmado.viajoEn', {count: perfil.data.solicitudes})}
          </p>
        )}
      </section>
    </PerfilCargoChrome>
  )
}
